/**
 * Mega Menu Lazy Loader
 * Loads content from hidden mega-menu section into lazy menu containers
 * This fixes missing navigation content in mega menu dropdowns
 */

(function() {
  'use strict';

  // Track which menus have been loaded to prevent duplicate loading
  const loadedMenus = new Set();

  /**
   * Load menu content from mega-menu section into lazy menu container
   * @param {HTMLElement} lazyMenuContainer - The .t4s-lazy_menu element
   */
  function loadMenuContent(lazyMenuContainer) {
    const menuId = lazyMenuContainer.getAttribute('data-id');
    if (!menuId) {
      console.warn('Mega Menu Lazy Loader: No data-id attribute found on lazy menu container');
      return;
    }

    // Check if already loaded - both by tracking Set and by checking for existing content
    if (loadedMenus.has(menuId)) {
      return;
    }

    // Also check if content already exists in the container (prevent duplicates)
    // Look for menu items or any content beyond just the loading background
    const existingContent = lazyMenuContainer.querySelector('.t4s-sub-column-item, .type_mn_link, .type_mn_banner, .type_mn_pr');
    if (existingContent) {
      // Content already loaded, mark as loaded and return
      loadedMenus.add(menuId);
      return;
    }

    // Find the source content in the hidden mega-menu section
    const sourceElement = document.getElementById('t4s-mega-contents' + menuId);
    if (!sourceElement) {
      console.warn('Mega Menu Lazy Loader: Source content not found for menu ID:', menuId);
      return;
    }

    // Clear the loading background first
    const loadingBg = lazyMenuContainer.querySelector('.t4s-loading--bg');
    if (loadingBg) {
      loadingBg.remove();
    }

    // Clone only the children of the source element, not the wrapper itself
    // This prevents duplicate wrapper divs and ensures proper structure
    const fragment = document.createDocumentFragment();
    Array.from(sourceElement.children).forEach(function(child) {
      // Clone each child node (menu items, styles, etc.)
      const clonedChild = child.cloneNode(true);
      
      // Remove any IDs from cloned elements to avoid duplicate IDs
      if (clonedChild.id) {
        clonedChild.removeAttribute('id');
      }
      // Also check for IDs in nested elements
      const nestedElementsWithIds = clonedChild.querySelectorAll('[id]');
      nestedElementsWithIds.forEach(function(el) {
        // Keep IDs that are unique (like block IDs), but remove wrapper IDs
        if (el.id.startsWith('t4s-mega-contents')) {
          el.removeAttribute('id');
        }
      });
      
      fragment.appendChild(clonedChild);
    });

    // Append all cloned children to the lazy menu container
    lazyMenuContainer.appendChild(fragment);

    // Mark as loaded
    loadedMenus.add(menuId);

    // Initialize isotope if needed
    if (lazyMenuContainer.classList.contains('isotopet4s-later')) {
      // Trigger theme's isotope initialization
      if (window.t4s && typeof window.t4s.isotope === 'function') {
        try {
          window.t4s.isotope(lazyMenuContainer);
        } catch (e) {
          // Isotope initialization failed, but content is loaded
          console.warn('Mega Menu Lazy Loader: Isotope initialization failed:', e);
        }
      }
    }

    // Trigger any custom events for menu loaded
    const event = new CustomEvent('megaMenuLoaded', {
      detail: { menuId: menuId, container: lazyMenuContainer }
    });
    document.dispatchEvent(event);
  }

  /**
   * Initialize lazy menu loading
   */
  function initLazyMenuLoading() {
    // Find all lazy menu containers
    const lazyMenus = document.querySelectorAll('.t4s-lazy_menu');
    
    if (lazyMenus.length === 0) {
      return;
    }

    // Load menus on hover/focus of parent menu item
    lazyMenus.forEach(function(lazyMenu) {
      // Find the parent menu item
      const menuItem = lazyMenu.closest('.t4s-menu-item.has--children');
      if (!menuItem) {
        // If no parent found, try to find by traversing up
        let parent = lazyMenu.parentElement;
        while (parent && !parent.classList.contains('t4s-menu-item')) {
          parent = parent.parentElement;
        }
        if (parent) {
          setupMenuLoading(parent, lazyMenu);
        }
      } else {
        setupMenuLoading(menuItem, lazyMenu);
      }
    });

    // Also load menus that are already visible (for non-lazy scenarios)
    // Check if menu container is visible or if parent is hovered
    lazyMenus.forEach(function(lazyMenu) {
      const menuItem = lazyMenu.closest('.t4s-menu-item.has--children');
      if (menuItem) {
        const subMenu = menuItem.querySelector('.t4s-sub-menu');
        if (subMenu && (subMenu.classList.contains('t4s-op-1') || 
                        window.getComputedStyle(subMenu).opacity !== '0')) {
          loadMenuContent(lazyMenu);
        }
      }
    });
  }

  /**
   * Setup event listeners for menu loading
   * @param {HTMLElement} menuItem - The parent menu item
   * @param {HTMLElement} lazyMenu - The lazy menu container
   */
  function setupMenuLoading(menuItem, lazyMenu) {
    let loadTimeout;

    function handleMenuShow() {
      // Clear any pending timeout
      if (loadTimeout) {
        clearTimeout(loadTimeout);
      }

      // Load content with a small delay to avoid loading on every hover
      loadTimeout = setTimeout(function() {
        loadMenuContent(lazyMenu);
      }, 50);
    }

    // Load on mouseenter
    menuItem.addEventListener('mouseenter', handleMenuShow, { passive: true });
    
    // Load on focus (for keyboard navigation)
    const menuLink = menuItem.querySelector('a');
    if (menuLink) {
      menuLink.addEventListener('focus', handleMenuShow, { passive: true });
    }

    // Also load if menu is already visible
    const subMenu = menuItem.querySelector('.t4s-sub-menu');
    if (subMenu) {
      // Check if submenu is visible
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target;
            if (target.classList.contains('t4s-op-1') || 
                window.getComputedStyle(target).opacity !== '0') {
              loadMenuContent(lazyMenu);
            }
          }
        });
      });

      observer.observe(subMenu, {
        attributes: true,
        attributeFilter: ['class']
      });

      // Check initial state
      if (subMenu.classList.contains('t4s-op-1') || 
          window.getComputedStyle(subMenu).opacity !== '0') {
        loadMenuContent(lazyMenu);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLazyMenuLoading);
  } else {
    // DOM already loaded
    initLazyMenuLoading();
  }

  // Also reinitialize after dynamic content loads (e.g., AJAX navigation)
  document.addEventListener('contentLoaded', initLazyMenuLoading);
  window.addEventListener('load', initLazyMenuLoading);

})();
